import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface Options {
  /** Where focus lands when the trap activates. Defaults to the first focusable. */
  initialFocus?: RefObject<HTMLElement | null>;
  onEscape?: () => void;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && !el.hasAttribute('hidden'),
  );
}

/**
 * Keeps keyboard focus inside `ref` while `active`, closes on Escape, and
 * returns focus to whatever had it when the trap engaged. The first of its
 * kind in this codebase — the existing dialogs (BiomarkerLearnMore,
 * FeedbackLauncher) handle Escape and initial focus but let Tab walk out.
 *
 * Pair with useInert so assistive-technology virtual cursors are kept out of
 * the page behind the dialog as well.
 */
export default function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  { initialFocus, onEscape }: Options = {},
): void {
  useEffect(() => {
    if (!active) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const restoreTo = document.activeElement as HTMLElement | null;

    // Let the open transition mount before moving focus, so the browser does
    // not scroll a still-offscreen panel into view.
    const moveFocus = () => {
      const target = initialFocus?.current ?? focusables(node)[0] ?? node;
      target.focus({ preventScroll: true });
    };
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(moveFocus) : (moveFocus(), 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const list = focusables(node);
      if (list.length === 0) {
        event.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const current = document.activeElement;
      if (event.shiftKey) {
        if (current === first || !node.contains(current)) {
          event.preventDefault();
          last.focus();
        }
      } else if (current === last || !node.contains(current)) {
        event.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      node.removeEventListener('keydown', onKeyDown);
      if (restoreTo && typeof restoreTo.focus === 'function' && document.contains(restoreTo)) {
        restoreTo.focus({ preventScroll: true });
      }
    };
    // initialFocus / onEscape are read at engage time on purpose: re-running
    // the effect for a new callback identity would blur and refocus the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ref]);
}
